import React from 'react';
import { Box, Typography } from '@mui/material';
import { DISPLAY_STATUS, mapToDisplayStatus } from '../constants/claims';

interface StatusBadgeConfig {
  bg: string;
  color: string;
}

// Display status colors based on semantic meaning
const STATUS_CONFIG: Record<string, StatusBadgeConfig> = {
  [DISPLAY_STATUS.PENDING]:    { bg: '#E6F4F6', color: '#128293' },  // Teal (awaiting action)
  [DISPLAY_STATUS.INCOMPLETE]: { bg: '#FBEAEA', color: '#B02A37' },  // Red (needs attention)
  [DISPLAY_STATUS.PROCESSING]: { bg: '#E5F4EE', color: '#158A63' },  // Teal-green blend (in progress)
  [DISPLAY_STATUS.COMPLETED]:  { bg: '#E9F7ED', color: '#1E7E34' },  // Green (success)
  [DISPLAY_STATUS.CANCELED]:   { bg: '#FEF8E7', color: '#C4841C' },  // Amber (canceled)
  // General Documents (uploads) status — shares the success palette with COMPLETED.
  Complete:                    { bg: '#E9F7ED', color: '#1E7E34' },
  // Encounter-level intake statuses (feature 035).
  'Ready':                     { bg: '#E9F7ED', color: '#1E7E34' },  // Green (ready to bill)
  'Missing information':       { bg: '#FBEAEA', color: '#B02A37' },  // Red (needs attention)
  // Practice status (feature 043) — shares the success palette with COMPLETED/Ready.
  // 'Inactive' has no entry and falls through to the grey DEFAULT_CONFIG below.
  'Active':                    { bg: '#E9F7ED', color: '#1E7E34' },
};

// Grey fallback for unmapped statuses
const DEFAULT_CONFIG: StatusBadgeConfig = { bg: '#f5f5f5', color: '#616161' };

interface StatusBadgeProps {
  /** Raw status as returned by the backend (e.g. the SharePoint status 'In Coding'). */
  status: string;
}

/**
 * Displays a color-coded status badge. The backend stores the raw SharePoint
 * status, so we map it to the display vocabulary here (e.g. 'In Coding' →
 * 'Processing'). Unknown / already-display values pass through unchanged.
 */
const StatusBadge = React.memo(function StatusBadge({ status }: StatusBadgeProps) {
  const display = mapToDisplayStatus(status);
  const config = STATUS_CONFIG[display] ?? DEFAULT_CONFIG;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: config.bg,
        borderRadius: '9999px',
        px: '10px',
        py: '2px',
        height: 22,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: config.color,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: config.color,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {display}
      </Typography>
    </Box>
  );
});

export default StatusBadge;
